import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { NewsAndOffersPageFilterFormSchema } from '@/features/news-and-offers/validators/NewsAndOffersPageFilterValidator';
import { useNewsAndOffersFilter } from '@/features/news-and-offers/hooks/useNewsAndOffersFilter';
import EDatePicker from '@/components/everridge/EDatePicker';
import ETagPickerMulti from '@/components/everridge/ETagPickerMulti';
import {
	NewsAndOffersStatus,
	NewsAndOffersType,
} from '@/features/news-and-offers/types/news-and-offers-dto';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';

type FormData = z.infer<typeof NewsAndOffersPageFilterFormSchema>;

type Props = {
	onClose: () => void;
};

const NewsAndOffersPageFilter: FC<Props> = ({ onClose }) => {
	const [isOpen, setIsOpen] = useState(true);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();
	const { filter, setFilter } = useNewsAndOffersFilter();

	const { control, handleSubmit, reset } = useForm<FormData>({
		resolver: zodResolver(NewsAndOffersPageFilterFormSchema),
		values: {
			startDate: filter.startDate,
			endDate: filter.endDate,
			types: filter.types,
			statuses: filter.statuses,
		},
	});

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleSubmitFilterForm = (form: FormData) => {
		setFilter(form);
		handleClose();
	};

	const handleResetFilter = () => {
		reset({
			startDate: null,
			endDate: null,
			types: [],
			statuses: [],
		});
	};

	const newAndOfferTypes = Object.values(NewsAndOffersType).filter(
		(value) => typeof value === 'string',
	);

	const newAndOfferStatuses = Object.values(NewsAndOffersStatus).filter(
		(value) => typeof value === 'string',
	);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t('elements.components.filter.title')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<div className='flex gap-4'>
						<EDatePicker
							control={control}
							fieldName={'startDate'}
							label={t('elements.form.startDate.label')}
							placeholder={t(
								'elements.form.startDate.placeholder',
							)}
						/>
						<EDatePicker
							control={control}
							fieldName={'endDate'}
							label={t('elements.form.endDate.label')}
							placeholder={t('elements.form.endDate.placeholder')}
						/>
					</div>
					<ETagPickerMulti
						control={control}
						fieldName={'types'}
						label={t('elements.form.tagPicker.label')}
						tags={newAndOfferTypes}
					/>
					<ETagPickerMulti
						control={control}
						fieldName={'statuses'}
						label={t('elements.form.statusPicker.label')}
						tags={newAndOfferStatuses}
					/>
				</div>
				<DialogFooter
					className={cn('justify-between', isMobile && 'mt-auto')}>
					<Button
						variant='outline'
						onClick={handleResetFilter}>
						{t('elements.buttons.reset')}
					</Button>
					<Button onClick={handleSubmit(handleSubmitFilterForm)}>
						{t('elements.buttons.apply')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default NewsAndOffersPageFilter;
